import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "@/lib/i18n";
import { Workspace, useAuthLogin } from "@/lib/auth-store";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const registerSchema = z
  .object({
    fullName: z.string().min(2, "Name too short"),
    email: z.string().email("Invalid email"),
    password: z.string().min(8, "Min 8 characters"),
    confirmPassword: z.string(),
    terms: z.boolean().refine((value) => value === true, "Must agree to terms"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords mismatch",
    path: ["confirmPassword"],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

const DEFAULT_WORKSPACES: Workspace[] = [
  { id: "ws_prod", name: "Global Production", type: "Production" },
  { id: "ws_staging", name: "Staging Sandbox", type: "Staging" },
];

export function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthLogin();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { fullName: "", email: "", password: "", confirmPassword: "", terms: false },
  });

  const onRegisterSubmit = useCallback(
    async (data: RegisterFormValues) => {
      setIsLoading(true);
      try {
        const sessionId = `user-${data.email.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
        login({
          id: sessionId,
          email: data.email,
          fullName: data.fullName,
          role: "User",
          workspaces: DEFAULT_WORKSPACES,
          activeWorkspaceId: "ws_prod",
        });
        toast.success(`${t("register_title")} thành công`);
        navigate("/dashboard");
      } catch {
        toast.error("Đăng ký thất bại");
      } finally {
        setIsLoading(false);
      }
    },
    [login, navigate, t],
  );

  const termsVal = !!form.watch("terms");

  return (
    <form onSubmit={form.handleSubmit(onRegisterSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="fullName">{t("full_name")}</Label>
        <Input id="fullName" placeholder="Nguyen Van A" className="bg-secondary/50" {...form.register("fullName")} />
        {form.formState.errors.fullName ? <p className="text-xs text-destructive">{form.formState.errors.fullName.message}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">{t("email")}</Label>
        <Input id="email" type="email" placeholder="ten@congty.com" className="bg-secondary/50" {...form.register("email")} />
        {form.formState.errors.email ? <p className="text-xs text-destructive">{t("invalid_email")}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="password">{t("password")}</Label>
          <Input id="password" type="password" className="bg-secondary/50" {...form.register("password")} />
          {form.formState.errors.password ? <p className="text-xs text-destructive">{t("password_min")}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">{t("confirm_password")}</Label>
          <Input id="confirmPassword" type="password" className="bg-secondary/50" {...form.register("confirmPassword")} />
          {form.formState.errors.confirmPassword ? <p className="text-xs text-destructive">{t("password_mismatch")}</p> : null}
        </div>
      </div>

      <div className="flex items-center space-x-2 pt-2">
        <Checkbox id="terms" checked={termsVal} onCheckedChange={(value) => form.setValue("terms", !!value)} />
        <label htmlFor="terms" className="cursor-pointer text-xs font-medium leading-none">
          {t("agree_terms")}
        </label>
      </div>
      {form.formState.errors.terms ? <p className="text-xs text-destructive">{t("terms_required")}</p> : null}

      <Button type="submit" className="mt-4 w-full" disabled={isLoading}>
        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isLoading ? t("registering") : t("register_button")}
      </Button>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        {t("already_have_account")}{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          {t("sign_in")}
        </Link>
      </p>
    </form>
  );
}
